import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import { PosInformeDashboard } from '../models/pos-informe.models';
import { PosTransaccionesDashboard } from '../models/pos-transacciones.models';
import {
  ensureNoTopLevelErrors,
  GraphqlHttpResponse,
} from './mappers/pos-catalog.mapper';
import { mapPosInformeDashboard } from './mappers/pos-informe.mapper';
import { mapPosTransaccionesDashboard } from './mappers/pos-transacciones.mapper';

const VENTAS = `
  query PosVentasDashboard(
    $locationId: ID!
    $reportDate: ISO8601Date
    $startDatetime: ISO8601DateTime
    $endDatetime: ISO8601DateTime
  ) {
    posVentasDashboard(
      locationId: $locationId
      reportDate: $reportDate
      startDatetime: $startDatetime
      endDatetime: $endDatetime
    ) {
      locationId
      reportDate
      startDatetime
      endDatetime
      totalVentas
      totalPersonalizado
      totalGeneral
      totalPropinas
      totalDescuentoCarrito
      totalDescuentoArticulo
      totalEnvios
      totalReembolso
      ordenesCompletadas
      ordenesAnuladas
      hasData
      metodosPago
      ventasPorCategoria
      ventasPorProducto
      ordenesAnuladasDetalle
    }
  }
`;

const TX = `
  query PosTransaccionesDashboard(
    $locationId: ID!
    $page: Int
    $startDatetime: ISO8601DateTime
    $endDatetime: ISO8601DateTime
    $paymentMethods: [String!]
    $status: String
    $q: String
    $per: Int
  ) {
    posTransaccionesDashboard(
      locationId: $locationId
      page: $page
      startDatetime: $startDatetime
      endDatetime: $endDatetime
      paymentMethods: $paymentMethods
      status: $status
      q: $q
      per: $per
    ) {
      locationId
      startDatetime
      endDatetime
      payload
    }
  }
`;

const CANCEL = `
  mutation CancelPosOrder($id: ID!, $cancellationNote: String!, $idempotencyKey: String) {
    cancelPosOrder(id: $id, cancellationNote: $cancellationNote, idempotencyKey: $idempotencyKey) {
      order { id orderNumber status }
      errors
    }
  }
`;

@Injectable()
export class PosReportsApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string
  ) {}

  ventasDashboard(
    locationId: string,
    options: {
      reportDate?: string;
      startDatetime?: string;
      endDatetime?: string;
    } = {}
  ): Observable<PosInformeDashboard> {
    return this.http
      .post<GraphqlHttpResponse<{ posVentasDashboard: Record<string, unknown> }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: VENTAS,
          variables: {
            locationId,
            reportDate: options.reportDate ?? null,
            startDatetime: options.startDatetime ?? null,
            endDatetime: options.endDatetime ?? null,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          if (!d.posVentasDashboard) {
            throw new Error('Respuesta de informe vacía');
          }
          return mapPosInformeDashboard(d.posVentasDashboard);
        })
      );
  }

  transaccionesDashboard(
    locationId: string,
    options: {
      page?: number;
      per?: number;
      startDatetime?: string;
      endDatetime?: string;
      paymentMethods?: readonly string[];
      status?: string;
      q?: string;
    } = {}
  ): Observable<PosTransaccionesDashboard> {
    const methods = (options.paymentMethods ?? []).map((m) => m.trim()).filter(Boolean);
    return this.http
      .post<GraphqlHttpResponse<{ posTransaccionesDashboard: Record<string, unknown> }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: TX,
          variables: {
            locationId,
            page: options.page ?? 1,
            per: options.per ?? 50,
            startDatetime: options.startDatetime ?? null,
            endDatetime: options.endDatetime ?? null,
            paymentMethods: methods.length ? methods : null,
            status: options.status && options.status !== 'all' ? options.status : null,
            q: options.q?.trim() || null,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          if (!d.posTransaccionesDashboard) {
            throw new Error('Respuesta de transacciones vacía');
          }
          return mapPosTransaccionesDashboard(d.posTransaccionesDashboard);
        })
      );
  }

  cancelOrder(
    id: string,
    cancellationNote: string,
    idempotencyKey?: string | null
  ): Observable<{ errors: string[] }> {
    return this.http
      .post<GraphqlHttpResponse<{ cancelPosOrder: { errors: string[] } }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: CANCEL,
          variables: {
            id,
            cancellationNote,
            idempotencyKey: idempotencyKey ?? null,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => ({ errors: d.cancelPosOrder.errors ?? [] }))
      );
  }
}
